<template>
  <v-dialog
    :value="value"
    width="420"
    @input="$emit('input', $event)"
  >
    <v-card>
      <v-card-title class="text-subtitle-1">
        {{ schedule ? schedule.userName : '' }} の時間を設定
      </v-card-title>
      <v-card-text>
        <div class="d-flex align-center pt-1">
          <time-drum-field
            v-model="startTime"
            :options="timeOptions"
            label="出勤"
            clear-text="未定"
            class="me-4"
          />
          <time-drum-field
            v-model="endTime"
            :options="timeOptions"
            label="退勤"
            clear-text="未定"
          />
        </div>
        <p class="text-caption text--secondary mt-3 mb-0">
          退勤は未定のまま保存できます。両方空にすると「時間未定」に戻ります
        </p>
        <p
          v-if="error"
          class="error--text text-caption mt-2 mb-0"
        >
          {{ error }}
        </p>
      </v-card-text>
      <v-card-actions class="px-6 pb-4">
        <v-spacer></v-spacer>
        <v-btn
          text
          :disabled="submitting"
          @click="$emit('input', false)"
        >
          キャンセル
        </v-btn>
        <v-btn
          color="primary"
          :ripple="false"
          :loading="submitting"
          @click="save"
        >
          保存
        </v-btn>
      </v-card-actions>
    </v-card>
  </v-dialog>
</template>

<script>
import { ref, watch, getCurrentInstance } from '@vue/composition-api'
import { format } from 'date-fns'
import WorkScheduleApi from '@/api/waiter/WorkSchedule'
import { buildShiftDateTime } from '@/views/waiter/shiftStaffingUtils'
import { buildTimeOptions } from '@/utils/timeOptions'
import TimeDrumField from '@/views/components/time/TimeDrumField.vue'

export default {
  components: { TimeDrumField },
  props: {
    value: {
      type: Boolean,
      default: false,
    },
    // 対象シフト（{id, userName, startAt, endAt}）
    schedule: {
      type: Object,
      default: null,
    },
    // 営業日（yyyy-MM-dd）。深夜跨ぎの翌日補正に使う
    date: {
      type: String,
      default: null,
    },
  },
  setup(props, { emit }) {
    const vm = getCurrentInstance().proxy

    const startTime = ref('')
    const endTime = ref('')
    const error = ref('')
    const submitting = ref(false)

    watch(() => props.value, isOpen => {
      if (!isOpen) return
      startTime.value = props.schedule?.startAt ? format(new Date(props.schedule.startAt), 'HH:mm') : ''
      endTime.value = props.schedule?.endAt ? format(new Date(props.schedule.endAt), 'HH:mm') : ''
      error.value = ''
    })

    const save = async () => {
      error.value = ''
      const hasStart = startTime.value !== ''
      const hasEnd = endTime.value !== ''
      // 出勤のみ（退勤未定）は許容する。退勤だけの指定は不可
      if (hasEnd && !hasStart) {
        error.value = '退勤だけは設定できません。出勤時間も入力してください'

        return
      }

      const payload = { id: props.schedule.id }
      payload.startAt = hasStart ? buildShiftDateTime(props.date, startTime.value) : ''
      payload.endAt = hasEnd ? buildShiftDateTime(props.date, endTime.value, startTime.value) : ''

      submitting.value = true
      try {
        await WorkScheduleApi.updateWorkSchedule(payload)
        vm.$toast.success('時間を更新しました')
        emit('input', false)
        emit('saved')
      } catch (e) {
        const message = e?.response?.data?.message
        error.value = Array.isArray(message) ? message.join('、') : '時間の更新に失敗しました'
      } finally {
        submitting.value = false
      }
    }

    return {
      startTime,
      endTime,
      error,
      submitting,
      save,
      timeOptions: buildTimeOptions(),
    }
  },
}
</script>

<style scoped>
/* タブレット向け: Vuetifyのtext-caption(12px)をこの画面では読みやすいサイズに上書き */
.text-caption {
  font-size: 13px !important;
  font-weight: 500 !important;
}
</style>
